import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';

import { ServerUrls } from '../../app/ServerUrls';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { FlowDataGridCells } from '../../flow/FlowDataGridDefinitions';
import { serverScheduleUrl, siteScheduleUrl } from '../../schedule/utils/ScheduleUtils';
import { IScheduleFrequencyDescriptionFilter } from '../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { ISubscriptionInfo } from '../../ts/models/ISubscriptionInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { ISiteNameWithId } from '../../ts/ServerApi/types/ISiteNameWithId';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { itemsCellWithPopover } from './ItemsCellWithPopover';

export module RunFlowTasksGrid {

  export function columns(
    translate: IReactTranslateService,
    scope: DataGridCells.ISelectedItemsScope<RunFlowTaskInfo>,
    anyActionsAllowed: boolean,
    actions: IActionButton<RunFlowTaskInfo, {}>[],
    getActionTarget: () => IActionTarget<RunFlowTaskInfo, {}>,
    isViewingGridForAllSites: boolean,
    serverUrls: ServerUrls,
    siteUrls: SiteUrls,
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    showFlowNameColumn: boolean,
    showScheduleColumn: boolean,
    showSiteColumn: boolean,
    showPriorityColumn: boolean,
    showStatusColumn: boolean,
    isServerOrSiteAdmin: boolean,
    flowFailureThresholdForRunPrevention: number,
    )
  : DataGrid.Column[] {
    const columns: DataGrid.Column[] = [
      CommonDataGridColumns.selected(scope)
    ];

    if (showFlowNameColumn) {
      columns.push(CommonDataGridColumns.withExtraSpace({
        name: 'flow-name-col',
        dataKey: 'flowName',
        // TODO: enable sorting by flowName on backend
        // direction: 'asc',
        headerText: translate('flow'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: targetLinkFormatter(siteUrls, translate),
        sortable: false
      }));
    }

    columns.push(CommonDataGridColumns.withExtraSpace({
      name: 'output-steps-col',
      dataKey: 'flowOutputSteps',
      headerText: translate('outputSteps'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      // TFSID 843765: if all-sites don't show detailed output step info because we can't reliably fetch data connections
      cellFormatter: isViewingGridForAllSites
        ? itemsCellWithPopover(getOutputStepNamesForRow, 'outputStepsCount', translate)
        : FlowDataGridCells.FlowTaskOutputStepsWithIconsCell()
    }));

    if (showScheduleColumn) {
      columns.push({
        name: 'schedule-name-col',
        dataKey: 'scheduleName',
        direction: 'asc',
        headerText: translate('column_task_schedule'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.ScheduleDescriptionCell(
          isServerOrSiteAdmin
            ? (row: DataGrid.Row) => {
                const subscription = <ISubscriptionInfo>row.data;
                const schedule = subscription.schedule;

                return isViewingGridForAllSites
                  ? serverScheduleUrl(serverUrls, schedule.id, schedule.scheduledAction)
                  : siteScheduleUrl(siteUrls, schedule.id, schedule.scheduledAction);
              }
            : null,
          scheduleFrequencyDescriptionFilter)
      });
    }

    if (showPriorityColumn) {
      columns.push({
        name: 'priority-col',
        dataKey: 'priority',
        direction: 'asc',
        headerText: translate('column_task_priority'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.LocalizedCount('priority')
      });
    }

    if (showStatusColumn) {
      columns.push({
        name: 'status-col',
        dataKey: 'status',
        sortable: false,
        headerText: translate('flowRunHistoryColumn_status'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: FlowDataGridCells.FlowTaskStatusWithIconsCell(siteUrls, flowFailureThresholdForRunPrevention),
      });
    }

    if (showSiteColumn) {
      columns.push({
        name: 'site-name-col',
        dataKey: 'siteName',
        direction: 'asc',
        headerText: translate('column_task_site'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCellWithTransform('site',
          (site: ISiteNameWithId) => site.name)
      });
    }

    columns.push({
      name: 'last-run-at-col',
      dataKey: 'lastRunAt',
      headerText: translate('lastRun'),
      // TODO enable sort by last run on backend
      //direction: 'desc',
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('lastRunAt', translate('never'))
    });

    columns.push({
      name: 'next-run-at-col',
      dataKey: 'runNextAt',
      headerText: translate('nextScheduledRun'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('runNextAt', translate('disabled'))
    });

    columns.push({
      name: 'errors-col',
      dataKey: 'errors',
      headerText: translate('flowRunHistoryColumn_errors'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowRunHistoryErrorsCell(siteUrls, anyActionsAllowed),
    });

    // Add the actions menu to whichever column was first (not including the 'selected' column)
    if (anyActionsAllowed) {
      columns[1] = CommonDataGridColumns.withActions(columns[1], {
        actions: actions,
        getActionTarget: getActionTarget,
        translate: translate
      }, scope);
    }

    return columns;
  }

  function getOutputStepNamesForRow(row: DataGrid.Row): ReadonlyArray<INamedItemInfo> {
    const runFlowTaskInfo = row.data as RunFlowTaskInfo;
    if (runFlowTaskInfo && runFlowTaskInfo.flowOutputSteps) {
      return runFlowTaskInfo.flowOutputSteps;
    }
    return [];
  }


  function targetLinkFormatter(siteUrls: SiteUrls, translate: IReactTranslateService) {
    return DataGridCells.Link((row: DataGrid.Row) => {
      const runFlowTask = <RunFlowTaskInfo>row.data;
      const siteUrlsForRow = siteUrls.forSiteName(runFlowTask.site.urlName);
      if (row.data.flow) {
        const flow: INamedItemInfo = row.data.flow;
        return {
          text: flow.name,
          url: siteUrlsForRow.flowUrl(flow.id),
          iconClassName: 'tb-grid-icon-flow',
          iconTitle: translate('flow')
        };
      }
    });
  }
}
